import React, { createContext, lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, NavLink, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { ContentSettingsProvider } from './context/ContentSettingsContext';
import { NotificationProvider } from './context/NotificationContext';
import { useAuth } from './context/AuthContext';
import { useCart } from './context/CartContext';
import { useWishlist } from './context/WishlistContext';

import { FiArrowUp, FiHome, FiGrid, FiHeart, FiShoppingCart, FiUser } from 'react-icons/fi';

import { initMetaPixel, trackPageView } from './utils/metaPixelSdk';

import Header from './components/Header';
import Footer from './components/Footer';

const Home = lazy(() => import('./Pages/Home'));
const ProductListing = lazy(() => import('./Pages/ProductListing'));
const Productsdetailsh = lazy(() => import('./Pages/Productsdetailsh'));
const CartPage = lazy(() => import('./Pages/CartPage'));
const CheckoutPage = lazy(() => import('./Pages/CheckoutPage'));
const WishlistPage = lazy(() => import('./Pages/WishlistPage'));
const LoginPage = lazy(() => import('./Pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./Pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./Pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./Pages/auth/ResetPasswordPage'));
const PrivacyPolicy = lazy(() => import('./Pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./Pages/TermsOfService'));
const Policies = lazy(() => import('./Pages/Policies'));
const ShippingPolicy = lazy(() => import('./Pages/ShippingPolicy'));
const ContactUs = lazy(() => import('./Pages/ContactUs'));
const AboutUs = lazy(() => import('./Pages/AboutUs'));
const HelpCenter = lazy(() => import('./Pages/HelpCenter'));
const OrderTracking = lazy(() => import('./Pages/OrderTracking'));
const PaymentGateway = lazy(() => import('./components/PaymentGateway'));
const SummaryPage = lazy(() => import('./Pages/SummaryPage'));
const Profile = lazy(() => import('./Pages/Profile'));
const Orders = lazy(() => import('./Pages/Orders'));
const AdminDashboard = lazy(() => import('./Pages/AdminDashboard'));
const AdminReviews = lazy(() => import('./Pages/AdminReviews'));
const AdminSettings = lazy(() => import('./Pages/AdminSettings'));

// Create MyContext for backward compatibility
export const MyContext = createContext();

// Module-level flags - survive React StrictMode double-mount
let pixelInitDone = false;
let initialPageViewFired = false;

const MetaPixelRouteTracker = () => {
    const location = useLocation();

    // Initialize Meta Pixel and fire the first PageView ONCE per app load
    useEffect(() => {
        if (pixelInitDone) return;
        pixelInitDone = true;

        initMetaPixel();

        // Small delay so fbq script has time to load
        setTimeout(() => {
            if (!initialPageViewFired) {
                initialPageViewFired = true;
                const path = window.location.pathname;
                const lowerPath = String(path || '').toLowerCase();
                if (!(lowerPath.startsWith('/product/') || lowerPath.includes('/product/') || lowerPath === '/product')) {
                    trackPageView(path);
                }
            }
        }, 150);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Track PageView on every route change (after initial load)
    const lastPathRef = useRef('');

    useEffect(() => {
        const currentPath = location.pathname;

        // Skip if this is the very first render (initial PageView handled above)
        if (!initialPageViewFired) return;

        // Skip if path hasn't changed
        if (lastPathRef.current === currentPath) return;

        lastPathRef.current = currentPath;

        const lowerPath = String(currentPath || '').toLowerCase();
        if (lowerPath.startsWith('/product/') || lowerPath.includes('/product/') || lowerPath === '/product') {
            return;
        }

        trackPageView(currentPath);
    }, [location.pathname]);

    return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    const redirectTo = `${location.pathname}${location.search || ''}`;
    return isAuthenticated
        ? children
        : <Navigate to={`/login?redirect=${encodeURIComponent(redirectTo)}`} replace />;
};

const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();
    const location = useLocation();
    if (loading) return null;
    const redirectTo = `${location.pathname}${location.search || ''}`;

    if (!isAuthenticated) {
        return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTo)}`} replace />;
    }

    return String(user?.role || '').toLowerCase() === 'admin'
        ? children
        : <Navigate to="/" replace />;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    if (loading) return null;
    return !isAuthenticated ? children : <Navigate to="/" replace />;
};

const ScrollToTopButton = ({ show, mainScrollRef }) => {
    const location = useLocation();
    const pathname = String(location?.pathname || '');
    const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');
    if (!show || isAdminRoute) return null;

    const handleScrollTop = () => {
        const el = mainScrollRef?.current;
        if (el && typeof el.scrollTo === 'function') {
            el.scrollTo({ top: 0, behavior: 'smooth' });
        }
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const btnStyle = {
        background: 'linear-gradient(135deg, #0b4334, #065f46)',
        boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
        border: 'none',
        borderRadius: '50%',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    };

    const button = (
        <>
            <button
                type="button"
                onClick={handleScrollTop}
                className="fixed md:hidden bottom-20 right-4 z-[99999] h-11 w-11 text-white flex items-center justify-center hover:scale-110 active:scale-95"
                style={btnStyle}
                aria-label="Scroll to top"
            >
                <FiArrowUp className="text-lg" />
            </button>
            <button
                type="button"
                onClick={handleScrollTop}
                className="hidden md:flex fixed bottom-8 right-6 z-[99999] h-11 w-11 text-white items-center justify-center hover:scale-110 active:scale-95"
                style={btnStyle}
                aria-label="Scroll to top"
            >
                <FiArrowUp className="text-lg" />
            </button>
        </>
    );

    if (typeof document !== 'undefined' && document?.body) {
        return createPortal(button, document.body);
    }

    return button;
};

const MobileBottomNav = () => {
    const location = useLocation();
    const { itemCount } = useCart();
    const { itemCount: wishlistCount } = useWishlist();
    const { isAuthenticated } = useAuth();

    const pathname = String(location?.pathname || '');
    const hideOn = (
        pathname.startsWith('/product/') ||
        pathname === '/checkout' ||
        pathname === '/payment' ||
        pathname === '/summary' ||
        pathname === '/cart' ||
        pathname === '/login' ||
        pathname === '/register' ||
        pathname === '/forgot-password' ||
        pathname === '/reset-password'
    );

    if (hideOn) return null;

    const Badge = ({ value }) => {
        const v = Number(value || 0);
        if (!v) return null;
        return (
            <span
                className="absolute -top-1 -right-1.5 min-w-[16px] h-[16px] px-0.5 rounded-full text-white text-[9px] leading-[16px] text-center font-bold"
                style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
            >
                {v > 99 ? '99+' : v}
            </span>
        );
    };

    return (
        <nav
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]"
            style={{
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderTop: '1px solid rgba(241,245,249,0.8)',
                boxShadow: '0 -8px 32px rgba(0,0,0,0.08)',
            }}
        >
            <div className="grid grid-cols-5 px-1">
                {[
                    { to: '/', icon: FiHome, label: 'Home', end: true },
                    { to: '/ProductListing', icon: FiGrid, label: 'Shop' },
                    { to: '/cart', icon: FiShoppingCart, label: 'Cart', badge: itemCount },
                    { to: '/wishlist', icon: FiHeart, label: 'Wishlist', badge: wishlistCount },
                    { to: isAuthenticated ? '/profile' : '/login', icon: FiUser, label: 'Account' },
                ].map(({ to, icon: Icon, label, badge, end }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) =>
                            `flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 text-[10px] font-semibold transition-colors ${
                                isActive ? 'text-emerald-600' : 'text-slate-400'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className="relative">
                                    <span
                                        className={`flex items-center justify-center w-8 h-8 rounded-2xl transition-all ${
                                            isActive
                                                ? 'text-white'
                                                : ''
                                        }`}
                                        style={isActive ? { background: 'linear-gradient(135deg, #0b4334, #065f46)' } : {}}
                                    >
                                        <Icon className="text-[17px]" />
                                    </span>
                                    {badge > 0 && <Badge value={badge} />}
                                </span>
                                <span>{label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
        </nav>
    );
};

const ProductDetailsRoute = () => {
    const { id } = useParams();
    return <Productsdetailsh key={id} />;
};

// Auth routes where header/footer/nav should be hidden
const AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/otp-verification', '/checkout', '/payment', '/summary'];

// Inner layout wrapper — uses useLocation (must be inside <Router>)
const AppLayout = ({ children, headerRef, mainScrollRef, isHeaderHidden, isHeaderElevated, headerHeight, showScrollToTop, setHeaderHeight }) => {
    const location = useLocation();
    const isAuthPage = AUTH_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + '?'));

    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el) return;

        const measure = () => {
            const h = el.offsetHeight || 0;
            setHeaderHeight(h);
        };

        measure();

        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => measure());
            ro.observe(el);
        } else {
            window.addEventListener('resize', measure);
        }

        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', measure);
        };
    }, [location.pathname, headerRef, setHeaderHeight]);

    return (
        <div className="App flex h-screen overflow-hidden">
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header — hidden on auth pages */}
                {!isAuthPage && (
                    <>
                        <Header ref={headerRef} hidden={isHeaderHidden} elevated={isHeaderElevated} />
                        <div
                            aria-hidden="true"
                            className="flex-shrink-0 h-[70px] md:h-[70px]"
                            style={headerHeight > 0 ? { height: headerHeight } : undefined}
                        />
                    </>
                )}

                {/* Main Content */}
                <main
                    ref={mainScrollRef}
                    className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative ${isAuthPage ? '' : 'pb-16 md:pb-0'}`}
                    style={{
                        background: isAuthPage ? '#F1F3F6' : '#f8fafc',
                        '--app-header-height': `${headerHeight}px`,
                    }}
                >
                    {children}
                    {/* Footer — hidden on auth pages */}
                    {!isAuthPage && <Footer />}
                </main>

                {!isAuthPage && (
                    <>
                        <ScrollToTopButton show={showScrollToTop} mainScrollRef={mainScrollRef} />
                        <MobileBottomNav />
                    </>
                )}
            </div>
        </div>
    );
};

function App() {
    // Basic MyContext functionality for backward compatibility
    const [isCartPanelOpen, setIsCartPanelOpen] = React.useState(false);

    const headerRef = useRef(null);
    const mainScrollRef = useRef(null);
    const [isHeaderHidden, setIsHeaderHidden] = useState(false);
    const [isHeaderElevated, setIsHeaderElevated] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [showScrollToTop, setShowScrollToTop] = useState(false);



    useEffect(() => {
        const el = mainScrollRef.current;
        const hasEl = Boolean(el);

        let lastScrollTop = el?.scrollTop || 0;
        let ticking = false;
        // eslint-disable-next-line no-unused-vars
        const threshold = 8;
        // eslint-disable-next-line no-unused-vars
        const topRevealOffset = 12;

        const getWindowScrollTop = () => {
            if (typeof window === 'undefined') return 0;
            return (window.scrollY || document.documentElement?.scrollTop || document.body?.scrollTop || 0);
        };

        const computeTriggerPassed = (extraScrollTop = 0) => {
            const currentPath = (typeof window !== 'undefined' && window?.location?.pathname)
                ? String(window.location.pathname)
                : '';

            const marker = typeof document !== 'undefined'
                ? document.querySelector('[data-scroll-top-trigger="1"]')
                : null;

            const containerScrollTop = el?.scrollTop || 0;
            const windowScrollTop = getWindowScrollTop();
            const inferredScrollTop = Math.max(containerScrollTop, windowScrollTop, Number(extraScrollTop || 0));

            if (currentPath !== '/') {
                return {
                    passedContainer: inferredScrollTop > 200,
                    passedWindow: inferredScrollTop > 200,
                };
            }

            if (!marker || typeof marker.getBoundingClientRect !== 'function') {
                return {
                    passedContainer: containerScrollTop > 320,
                    passedWindow: windowScrollTop > 320,
                };
            }

            const markerRect = marker.getBoundingClientRect();
            const passedWindow = markerRect.top < 0;

            let passedContainer = false;
            if (el) {
                const containerRect = el.getBoundingClientRect();
                passedContainer = markerRect.top < containerRect.top;
            }

            return { passedContainer, passedWindow };
        };

        const onScroll = (evt) => {
            const containerScrollTop = el?.scrollTop || 0;
            const windowScrollTop = getWindowScrollTop();
            const targetScrollTop = (evt && evt.target && typeof evt.target.scrollTop === 'number') ? evt.target.scrollTop : 0;
            const currentScrollTop = Math.max(containerScrollTop, windowScrollTop, targetScrollTop);
            if (ticking) return;
            ticking = true;

            window.requestAnimationFrame(() => {
                // eslint-disable-next-line no-unused-vars
                const delta = currentScrollTop - lastScrollTop;

                setIsHeaderElevated(currentScrollTop > 4);
                const passed = computeTriggerPassed(targetScrollTop);
                setShowScrollToTop(Boolean(passed.passedContainer || passed.passedWindow));

                setIsHeaderHidden(false);

                lastScrollTop = currentScrollTop;
                ticking = false;
            });
        };

        if (hasEl) {
            el.addEventListener('scroll', onScroll, { passive: true });
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', onScroll, { passive: true });
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('scroll', onScroll, { passive: true, capture: true });
        }

        const initialPassed = computeTriggerPassed();
        setShowScrollToTop(Boolean(initialPassed.passedContainer || initialPassed.passedWindow));
        return () => {
            if (hasEl) el.removeEventListener('scroll', onScroll);
            if (typeof window !== 'undefined') window.removeEventListener('scroll', onScroll);
            if (typeof document !== 'undefined') document.removeEventListener('scroll', onScroll, { capture: true });
        };
    }, []);

    const toggleCartPanel = (open) => {
        setIsCartPanelOpen(open);
    };

    const handleClickOpenProductDetailsModal = (_product) => {
        // Reserved for future product quick-view modal
    };

    const myContextValue = {
        isCartPanelOpen,
        toggleCartPanel,
        handleClickOpenProductDetailsModal,
    };

    return (
        <AuthProvider>
            <ContentSettingsProvider>
                <CartProvider>
                    <WishlistProvider>
                        <NotificationProvider>
                            <MyContext.Provider value={myContextValue}>
                                <Router
                                    future={{
                                        v7_startTransition: true,
                                        v7_relativeSplatPath: true,
                                    }}
                                >
                                <MetaPixelRouteTracker />
                                    <AppLayout
                                        headerRef={headerRef}
                                        mainScrollRef={mainScrollRef}
                                        isHeaderHidden={isHeaderHidden}
                                        isHeaderElevated={isHeaderElevated}
                                        headerHeight={headerHeight}
                                        showScrollToTop={showScrollToTop}
                                        setHeaderHeight={setHeaderHeight}
                                    >
                                        <Suspense fallback={
                                            <div className="flex items-center justify-center h-64">
                                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                                            </div>
                                        }>
                                                    <Routes>
                                                        {/* Public Routes */}
                                                        <Route path="/" element={<Home />} />
                                                        <Route path="/ProductListing" element={<ProductListing />} />
                                                        <Route path="/ProductListing/:category" element={<ProductListing />} />
                                                        <Route path="/product/:id" element={<ProductDetailsRoute />} />
                                                        <Route path="/policies" element={<Policies />} />
                                                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                                        <Route path="/terms-of-service" element={<TermsOfService />} />
                                                        <Route path="/shipping-policy" element={<ShippingPolicy />} />
                                                        <Route path="/contact" element={<ContactUs />} />
                                                        <Route path="/about" element={<AboutUs />} />
                                                        <Route path="/help-center" element={<HelpCenter />} />
                                                        <Route path="/order-tracking" element={<OrderTracking />} />

                                                        {/* Auth Routes */}
                                                        <Route
                                                            path="/login"
                                                            element={
                                                                <PublicRoute>
                                                                    <LoginPage />
                                                                </PublicRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/register"
                                                            element={
                                                                <PublicRoute>
                                                                    <RegisterPage />
                                                                </PublicRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/forgot-password"
                                                            element={
                                                                <PublicRoute>
                                                                    <ForgotPasswordPage />
                                                                </PublicRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/reset-password"
                                                            element={
                                                                <PublicRoute>
                                                                    <ResetPasswordPage />
                                                                </PublicRoute>
                                                            }
                                                        />

                                                        {/* Cart & Wishlist - accessible without login (guest mode) */}
                                                        <Route
                                                            path="/cart"
                                                            element={<CartPage />}
                                                        />
                                                        <Route
                                                            path="/wishlist"
                                                            element={<WishlistPage />}
                                                        />
                                                        <Route
                                                            path="/profile"
                                                            element={
                                                                <ProtectedRoute>
                                                                    <Profile />
                                                                </ProtectedRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/orders"
                                                            element={
                                                                <ProtectedRoute>
                                                                    <Orders />
                                                                </ProtectedRoute>
                                                            }
                                                        />
                                                        {/* Checkout accessible without login; login enforced at payment step */}
                                                        <Route
                                                            path="/checkout"
                                                            element={<CheckoutPage />}
                                                        />
                                                        <Route
                                                            path="/payment"
                                                            element={
                                                                <ProtectedRoute>
                                                                    <PaymentGateway />
                                                                </ProtectedRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/summary"
                                                            element={
                                                                <ProtectedRoute>
                                                                    <SummaryPage />
                                                                </ProtectedRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin"
                                                            element={
                                                                <AdminRoute>
                                                                    <AdminDashboard />
                                                                </AdminRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin/reviews"
                                                            element={
                                                                <AdminRoute>
                                                                    <AdminReviews />
                                                                </AdminRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin/settings"
                                                            element={
                                                                <AdminRoute>
                                                                    <AdminSettings />
                                                                </AdminRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin/products"
                                                            element={
                                                                <AdminRoute>
                                                                    <Navigate to="/admin" replace />
                                                                </AdminRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin/orders"
                                                            element={
                                                                <AdminRoute>
                                                                    <Navigate to="/admin" replace />
                                                                </AdminRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin/users"
                                                            element={
                                                                <AdminRoute>
                                                                    <Navigate to="/admin" replace />
                                                                </AdminRoute>
                                                            }
                                                        />
                                                        <Route
                                                            path="/admin/analytics"
                                                            element={
                                                                <AdminRoute>
                                                                    <Navigate to="/admin" replace />
                                                                </AdminRoute>
                                                            }
                                                        />

                                                        {/* Fallback */}
                                                        <Route path="*" element={<Navigate to="/" replace />} />
                                                    </Routes>
                                                </Suspense>
                                    </AppLayout>
                                </Router>
                            </MyContext.Provider>
                        </NotificationProvider>
                    </WishlistProvider>
                </CartProvider>
            </ContentSettingsProvider>
        </AuthProvider>
    );
}

export default App;